import React from "react";
import { useParams } from "react-router-dom";
import { LoadingV2, TabbedCard, TabbedCardContent } from "@ucl/library";
import { MessageComponent, Severity, Variant } from '@syncfusion/ej2-react-notifications';
import "./BankerApplicationViewPage.scss";
import variables from "@/config/variables";
import { StickyBankerApplicationDetailsForm } from "@/components/BankerApplicationDetails/StickyBankerApplicationDetailsForm";
import { BankerApplicationDetailsForm } from "@/components/BankerApplicationDetails/BankerApplicationDetailsForm";
import { BoltTaskGrid } from "@bolt/task";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { DocumentTemplateTaskExecutionTypeModule } from "@bolt/documenttemplates";
import { useAuthHeadersRequired } from "@/lib/auth/authUtils";
import { SanctionsMatchesGrid } from "@/components/SanctionsMatchesGrid";
import { CreditAuthorizationsGrid } from "@/components/CreditAuthorizationsGrid";
import { UnderwritingRuleResultsTable } from "@/components/UnderwritingRuleResultsTable";
import { BankerApplicationViewPageHeaderV2 } from "./BankerApplicationViewPageHeaderV2";
import { FinancialApplicationExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/FinancialApplicationExpressionSchemaTaskExecutionTypeForm";
import { JointAccountOwnerExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/JointAccountOwnerExpressionSchemaTaskExecutionTypeForm";
import { EntityTypes } from "@/constants/entityTypes";
import { KeyPersonExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/KeyPersonExpressionSchemaTaskExecutionTypeForm";
import { bankerApplicationViewStateAtomFamily } from "@/atoms/bankerApplicationViewStateAtomFamily";
import { useAtomValue } from "jotai";

export const BankerApplicationViewPage: React.FC = () => {
  const { financialApplicationId } = useParams<{ financialApplicationId: string }>();
  const getAuthRequestHeader = useAuthHeadersRequired();

  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId || "");
  const { coreData, refreshCoreData } = useFinancialApplicationCoreData(financialApplicationId || "");
  const [taskGridRefreshed, setTaskGridRefreshed] = React.useState<number>(0);

  const handleTasksChanged = () => {
    const ts = Date.now();
    setTaskGridRefreshed(ts);
    setTimeout(() => {
      refreshFinancialApplication();
    }, 1200);
  };

  const bankerApplicationViewState = useAtomValue(
    bankerApplicationViewStateAtomFamily(financialApplicationId || "")
  );
  const useStickyVersion = useAtomValue(bankerApplicationViewState.usesSticky);

  // Base tabs that are always shown
  const baseTabs: TabbedCardContent[] = [
    {
      tabKey: "details",
      headerContent: "Application Details",
      tabContent: financialApplicationId ? (
        useStickyVersion ? (
          <StickyBankerApplicationDetailsForm financialApplicationId={financialApplicationId} />
        ) : (
          <BankerApplicationDetailsForm financialApplicationId={financialApplicationId} />
        )
      ) : (
        <LoadingV2 />
      ),
    },
    {
      tabKey: "tasks",
      headerContent: "Tasks",
      tabContent: (
        <div className="banker-application-view__tab-content">
          <BoltTaskGrid
            gridLastRefreshed={taskGridRefreshed}
            onTasksChanged={handleTasksChanged}
            parentEntityId={financialApplicationId}
            parentEntityType={EntityTypes.FinancialApplication.EntityType}
            apiBaseUrl={variables.apiBaseUrl}
            gridKey="banker-task-grid"
            getAuthRequestHeader={getAuthRequestHeader}
            formRegistry={{
              FinancialApplicationExpressionSchema: {
                FormComponent: FinancialApplicationExpressionSchemaTaskExecutionTypeForm,
              },
              JointAccountOwnerExpressionSchema: {
                FormComponent: JointAccountOwnerExpressionSchemaTaskExecutionTypeForm,
              },
              KeyPersonExpressionSchema: {
                FormComponent: KeyPersonExpressionSchemaTaskExecutionTypeForm,
              },
            }}
            taskExecutionModuleRegistry={{
              DocumentTemplateTaskExecutionType: DocumentTemplateTaskExecutionTypeModule,
            }}
            taskExecutionLeftPanelIsDocked={false}
            taskExecutionRightPanelIsDocked={false}
            commentPermissions={{
              canComment: true,
              canEdit: false,
              canPin: false,
              canDelete: false,
            }}
          />
        </div>
      ),
    },
    {
      tabKey: "sanction-matches",
      headerContent: "Sanction Matches",
      tabContent: (
        <div className="banker-application-view__tab-content">
          <SanctionsMatchesGrid
            parentEntityId={financialApplicationId}
            parentEntityType="FinancialApplication"
            financialApplicationId={financialApplicationId}
            getAuthRequestHeader={getAuthRequestHeader}
          />
        </div>
      ),
    },
  ];

  // Credit-related tabs that are only enabled when credit products are selected
  const creditTabs: TabbedCardContent[] = [
    {
      tabKey: "credit-authorizations",
      headerContent: "Credit Authorizations",
      disabled: !coreData?.hasCreditAccountSelected,
      tabContent: (
        <div className="banker-application-view__tab-content">
          <CreditAuthorizationsGrid
            parentEntityId={financialApplicationId}
            parentEntityType="FinancialApplication"
            getAuthRequestHeader={getAuthRequestHeader}
            financialApplicationId={financialApplicationId}
            isApplicantLinked={!!coreData?.applicantUserId}
          />
        </div>
      ),
    },
    {
      tabKey: "underwriting-rules",
      headerContent: "Underwriting Rules",
      disabled: !coreData?.hasCreditAccountSelected,
      tabContent: (
        <div className="banker-application-view__tab-content">
          <UnderwritingRuleResultsTable
            parentEntityId={financialApplicationId || ""}
            parentEntityType="FinancialApplication"
          />
        </div>
      ),
    },
  ];

  const tabsData = [...baseTabs, ...creditTabs];

  const collaborationMessageContent: React.FC<{}> = () => (
    <>Before the applicant can complete tasks, provide information, or authorize a credit check, you need to share the application with them. Click <strong>"Share Application"</strong> on this page to begin.</>
  );

  return (
    <div className="banker-application-view">
      {/* Show message when applicantUserId is not set */}
      {coreData && !coreData.applicantUserId && (
        <div className="banker-application-view__message-container">
          <MessageComponent
            severity={Severity.Warning}
            content={collaborationMessageContent}
            cssClass="banker-application-view__collaboration-message"
            showCloseIcon
            variant={Variant.Text}
          />
        </div>
      )}

      {financialApplicationId && (
        <BankerApplicationViewPageHeaderV2 
          financialApplicationId={financialApplicationId} 
          onCoreDataRefresh={refreshCoreData}
        />
      )}
      
      <div className="banker-application-view__content">
        <TabbedCard
          tabs={tabsData}
          className="banker-application-view__tabbed-card"
          basePath={"/banker/applications/" + financialApplicationId}
        />
      </div>
    </div>
  );
};

export default BankerApplicationViewPage;
